import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { PaleState } from '@/interfaces';
import { filtersToBrowserQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { stringifyParams } from '@/lib/utils/queryString';

export const SmallGeoMap = () => {
    const filters = useSelector((s: PaleState) => s.filters);
    const boundingBox = filters.boundingBox;
    const destinationLabel = filters.destinationLabel;

    // Calculate center point from bounding box
    const mapCenter = useMemo(() => {
        if (boundingBox) {
            const lat = (boundingBox.minLat + boundingBox.maxLat) / 2;
            const lng = (boundingBox.minLon + boundingBox.maxLon) / 2;
            return { lat, lng };
        }
        // Default to somewhere in Southern Europe (good for package tours)
        return { lat: 40, lng: 15 };
    }, [boundingBox]);

    // Calculate zoom level based on bounding box size
    const zoomLevel = useMemo(() => {
        if (!boundingBox) return 4;
        const latDiff = Math.abs(boundingBox.maxLat - boundingBox.minLat);
        const lngDiff = Math.abs(boundingBox.maxLon - boundingBox.minLon);
        const maxDiff = Math.max(latDiff, lngDiff);

        if (maxDiff > 20) return 3;
        if (maxDiff > 10) return 4;
        if (maxDiff > 5) return 5;
        if (maxDiff > 2) return 6;
        if (maxDiff > 1) return 7;
        return 8;
    }, [boundingBox]);

    const staticMapImageUrl = `https://maptiles.finncdn.no/staticmap?lat=${mapCenter.lat}&lng=${mapCenter.lng}&zoom=${zoomLevel}&maptype=norwayVector&showPin=false&size=590x194`;

    const mapUrlWithFilters = useMemo(() => {
        const queryString = stringifyParams(filtersToBrowserQueryParams(filters));
        return queryString ? `/reise/pakkereiser/kart?${queryString}` : '/reise/pakkereiser/kart';
    }, [filters]);

    return (
        <a href={mapUrlWithFilters} className="block border rounded-8 mb-16" data-testid="smallGeoMap" aria-label="Vis i kart">
            <img
                src={staticMapImageUrl}
                alt={destinationLabel ? `Kart over ${destinationLabel}` : 'Vis på kart'}
                className="w-full rounded-t-8 cursor-pointer hover:opacity-90 transition-opacity"
            />
            <p className="text-center py-12 m-0">Vis i kart</p>
        </a>
    );
};
